/**
 * Compose, Reply, Reply All and Forward (piece 32, Compose; decisions 0081,
 * 0791): what the composer reads and writes, the addresses it offers, the
 * quoted history a reply carries and the header block a composed message goes
 * out with, typed once so the server's answer and the screen's fixture are the
 * same shape.
 */
import { escapeHtml } from '../controls/richText';
import type { Module } from '../permissions/modules';
import { mailHtmlFromText } from './body';
import { MAIL_API_PATH, MAIL_MESSAGES_PATH } from './client';
import { parseAddressList } from './headers';
import type { DeliveryStatusValue, MailboxKindValue, MessagePriorityValue } from './model';
import { isEmailAddress } from '../validators';

/** The composer's routes, relative to the API root (piece 1). */
export const MAIL_COMPOSE_SENDERS_PATH = `${MAIL_API_PATH}/compose/senders`;
export const MAIL_CONTACTS_PATH = `${MAIL_API_PATH}/contacts`;
export const MAIL_RECORD_FILES_PATH = `${MAIL_API_PATH}/record-files`;
export const MAIL_DRAFTS_PATH = `${MAIL_API_PATH}/drafts`;
export const mailDraftPath = (id: string): string => `${MAIL_DRAFTS_PATH}/${id}`;
export const mailDraftSendPath = (id: string): string => `${mailDraftPath(id)}/send`;
export const mailDraftAttachmentsPath = (id: string): string => `${mailDraftPath(id)}/attachments`;
/** Files already in the application, attached by id; a literal segment the server declares before an attachment's own id. */
export const mailDraftAttachFilesPath = (id: string): string => `${mailDraftAttachmentsPath(id)}/files`;
export const mailDraftAttachmentPath = (id: string, attachmentId: string): string => `${mailDraftAttachmentsPath(id)}/${attachmentId}`;
/** Reply, Reply All or Forward: a draft made from a message. */
export const mailRespondPath = (messageId: string): string => `${MAIL_MESSAGES_PATH}/${messageId}/respond`;

export const COMPOSE_MODES = ['new', 'reply', 'replyAll', 'forward'] as const;
export type ComposeMode = (typeof COMPOSE_MODES)[number];

export function isComposeMode(value: unknown): value is ComposeMode {
  return typeof value === 'string' && (COMPOSE_MODES as readonly string[]).includes(value);
}

/**
 * 🔒 Why the viewer may send a composed message from a mailbox (decisions 0081,
 * 0791, 0841): their own, a SendAs grant on a shared one, or a SendAs grant on
 * another person's, which sends on their behalf with the viewer named in
 * `Sender:`. 🔨 A tag never widens it: the module's entry in
 * `mail.moduleSenders` answers only mail the module sends.
 */
export type ComposeSenderVia = 'own' | 'sendAs' | 'onBehalf';

/** One mailbox the From picker offers. */
export interface ComposeSender {
  mailboxId: string;
  name: string;
  address: string;
  kind: MailboxKindValue;
  via: ComposeSenderVia;
}

export interface ComposeSendersAnswer {
  senders: ComposeSender[];
  /** The mailbox the composer opens on: the viewer's own, or their first SendAs grant where they have none. */
  defaultMailboxId: string | null;
}

/** A record a composed message is tagged to, with what the composer prints for it. */
export interface ComposeTag {
  recordType: string;
  recordId: string;
  label: string;
}

export interface ComposeAttachmentView {
  id: string;
  fileId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  scanStatus: 'Pending' | 'Clean' | 'Blocked';
}

/** A draft as the composer opens it. */
export interface ComposeDraftView {
  id: string;
  mailboxId: string;
  mailboxAddress: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
  priority: MessagePriorityValue;
  /** Compose asked for a read receipt: the send carries `Disposition-Notification-To` (RFC 3798). */
  readReceipt: boolean;
  tag: ComposeTag | null;
  attachments: ComposeAttachmentView[];
  /** The message this answers, where it is a reply or a forward. */
  inReplyTo: string | null;
  deliveryStatus: DeliveryStatusValue | null;
  scheduledSendAt: string | null;
}

/** What an autosave writes; a field left out is left as it stands. */
export interface ComposeDraftInput {
  mailboxId?: string;
  to?: readonly string[];
  cc?: readonly string[];
  bcc?: readonly string[];
  subject?: string;
  bodyHtml?: string;
  priority?: MessagePriorityValue;
  readReceipt?: boolean;
  tag?: { recordType: string; recordId: string } | null;
}

export type ComposeSendOutcome = 'Sent' | 'Held' | 'Failed';

export interface ComposeSendAnswer {
  id: string;
  outcome: ComposeSendOutcome;
  /** The release time of a held or scheduled message, as an instant. */
  scheduledSendAt: string | null;
}

/** One of a record's files the composer can attach. */
export interface ComposeRecordFile {
  fileId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

/** The longest address list one field carries, and the longest subject, so an autosave cannot grow a row without end. */
export const COMPOSE_MAX_RECIPIENTS = 100;
export const COMPOSE_MAX_SUBJECT = 998;

/** A typed list, commas or semicolons between, with the addresses Compose can send to and the ones it cannot. */
export function splitAddresses(value: string): { valid: string[]; invalid: string[] } {
  const parts = parseAddressList(value.replace(/;/g, ','));
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const part of parts) (isEmailAddress(part.address) ? valid : invalid).push(part.address);
  // A typed word with no @ at all never reaches parseAddressList; name it so the person sees why it was dropped.
  for (const word of value.split(/[,;]/).map((w) => w.trim()).filter((w) => w && !w.includes('@'))) invalid.push(word);
  return { valid: [...new Set(valid)], invalid };
}

/** An address list as an autosave carries it: valid, lower-cased, once each, and no longer than the cap. */
export function cleanAddressList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string') continue;
    const address = entry.trim().toLowerCase();
    if (isEmailAddress(address) && !out.includes(address)) out.push(address);
    if (out.length >= COMPOSE_MAX_RECIPIENTS) break;
  }
  return out;
}

const RE = /^\s*re\s*:/i;
const FWD = /^\s*(fwd?|fw)\s*:/i;

export function replySubject(subject: string): string {
  const s = subject.trim();
  return RE.test(s) ? s : `Re: ${s}`;
}

export function forwardSubject(subject: string): string {
  const s = subject.trim();
  return FWD.test(s) ? s : `Fwd: ${s}`;
}

/** What a reply reads off the message it answers. */
export interface ReplySource {
  direction: 'Inbound' | 'Outbound';
  fromAddress: string;
  replyToAddress: string | null;
  to: readonly string[];
  cc: readonly string[];
}

/**
 * 🔒 Who a reply goes to (piece 32, Compose). Reply answers Reply-To where the
 * sender set one and From otherwise; a reply to mail this mailbox sent goes
 * back to its recipients. Reply All adds everybody else on To and Cc, never
 * the mailbox replying.
 */
export function replyRecipients(source: ReplySource, ownAddress: string, all: boolean): { to: string[]; cc: string[] } {
  const own = ownAddress.toLowerCase();
  const lower = (list: readonly string[]) => list.map((a) => a.toLowerCase());
  const first = source.direction === 'Outbound' ? lower(source.to) : [(source.replyToAddress ?? source.fromAddress).toLowerCase()];
  const to = [...new Set(first.filter((a) => a !== own))];
  if (!all) return { to, cc: [] };
  const others = source.direction === 'Outbound' ? lower(source.cc) : [...lower(source.to), ...lower(source.cc)];
  const cc = [...new Set(others.filter((a) => a !== own && !to.includes(a)))];
  return { to, cc };
}


/**
 * 🔒 The quoted history Reply and Reply All carry, and the forwarded block
 * Forward carries (piece 32, Compose): the original under one line naming who
 * wrote it and when, in the viewer's own words for the time.
 */
export function quotedHistory(input: { mode: 'reply' | 'replyAll' | 'forward'; from: string; dateText: string; subject: string; to: readonly string[]; html: string; header?: 'Short' | 'Full' }): string {
  const html = input.html.trim() || mailHtmlFromText('');
  // 🔒 A person's Full reply header draws the sent message's From, Sent, To and Subject above the quote (decision 1236).
  if (input.mode !== 'forward' && input.header === 'Full') {
    const lines = [`From: ${input.from}`, `Sent: ${input.dateText}`, `To: ${input.to.join(', ')}`, `Subject: ${input.subject}`].map(escapeHtml).join('<br>');
    return `<p><br></p><p data-mail-quote-head="full">${lines}</p><blockquote data-mail-quote="true">${html}</blockquote>`;
  }
  if (input.mode === 'forward') {
    const lines = [`From: ${input.from}`, `Date: ${input.dateText}`, `Subject: ${input.subject}`, `To: ${input.to.join(', ')}`].map(escapeHtml).join('<br>');
    return `<p><br></p><div data-mail-forwarded="true"><p>---------- Forwarded message ----------<br>${lines}</p>${html}</div>`;
  }
  return `<p><br></p><p data-mail-quote-head="true">${escapeHtml(`On ${input.dateText}, ${input.from} wrote:`)}</p><blockquote data-mail-quote="true">${html}</blockquote>`;
}

/** An identity header's id, `<id@domain>`, for a message this client composes. */
export function composedMessageId(uuid: string, mailboxAddress: string): string {
  const domain = mailboxAddress.split('@')[1] || 'mail.invalid';
  return `<${uuid}@${domain}>`;
}

/** The header a composed message asks for a read receipt with (RFC 3798). */
export const READ_RECEIPT_HEADER = 'Disposition-Notification-To';

/** What a composed message carries in its header block beside its addresses and its body. */
export interface ComposedHeaderFacts {
  fromAddress: string;
  fromDisplayName: string | null;
  to: readonly string[];
  cc: readonly string[];
  subject: string;
  messageIdHeader: string;
  inReplyTo: string | null;
  references: readonly string[];
  priority: MessagePriorityValue;
  readReceipt: boolean;
  /** 🔒 The delegate who sends from another person's mailbox, named in `Sender:` while From stays the mailbox (decision 0841). */
  sender?: { address: string; displayName: string | null } | null;
}

const headerName = (name: string | null, address: string): string => (name ? `"${name.replace(/["\\\r\n]/g, '')}" <${address}>` : address);
const oneLine = (value: string): string => value.replace(/[\r\n]+/g, ' ');

/**
 * The headers a composed message is handed to the Outbound Mail Service with:
 * its identity, the thread it answers, the priority on `Importance`, and the
 * read-receipt request where Compose asked for one.
 */
export function composedTransportHeaders(facts: ComposedHeaderFacts): Record<string, string> {
  return {
    'Message-ID': facts.messageIdHeader,
    ...(facts.inReplyTo ? { 'In-Reply-To': facts.inReplyTo } : {}),
    ...(facts.references.length > 0 ? { References: facts.references.join(' ') } : {}),
    ...(facts.priority === 'High' ? { Importance: 'high' } : {}),
    ...(facts.readReceipt ? { [READ_RECEIPT_HEADER]: facts.fromAddress } : {}),
    ...(facts.sender && facts.sender.address.toLowerCase() !== facts.fromAddress.toLowerCase() ? { Sender: headerName(facts.sender.displayName, facts.sender.address) } : {}),
  };
}

/**
 * The header block a composed message stores as `rawHeaders`: what it was sent
 * with, which View original headers renders, and where a draft keeps its
 * read-receipt request between saves.
 */
export function composedRawHeaders(facts: ComposedHeaderFacts): string {
  const lines = [
    `From: ${headerName(facts.fromDisplayName, facts.fromAddress)}`,
    ...(facts.to.length > 0 ? [`To: ${facts.to.join(', ')}`] : []),
    ...(facts.cc.length > 0 ? [`Cc: ${facts.cc.join(', ')}`] : []),
    `Subject: ${oneLine(facts.subject)}`,
    ...Object.entries(composedTransportHeaders(facts)).map(([name, value]) => `${name}: ${oneLine(value)}`),
  ];
  return `${lines.join('\r\n')}\r\n`;
}

/** Whether a stored header block asks for a read receipt. */
export function asksReadReceipt(rawHeaders: string): boolean {
  return new RegExp(`^${READ_RECEIPT_HEADER}:`, 'im').test(rawHeaders);
}

/** Settings › Email › Sending (piece 15; decisions 0791, 0830): the module senders, and what the next steps add beside them. */
export const MAIL_SETTINGS_SENDING_PATH = `${MAIL_API_PATH}/settings/sending`;
export const MAIL_SETTINGS_MODULE_SENDERS_PATH = `${MAIL_API_PATH}/settings/module-senders`;
export const MAIL_SENDING_SETTINGS_ROUTE = '/settings/email/sending';

/** One mailbox a module's entry can name: a Department or Module mailbox, never a person's or a list. */
export interface ModuleSenderMailbox {
  id: string;
  name: string;
  address: string;
  kind: MailboxKindValue;
}

export interface SendingSettingsAnswer {
  /** One row per permission Module, with the mailbox its entry names, or null for the sending person's own. */
  moduleSenders: { module: Module; mailboxId: string | null }[];
  mailboxes: ModuleSenderMailbox[];
  /** The organisation's undo-send delay in seconds, which a person's own row overrides (decision 0833). */
  undoDelaySeconds?: number;
}

/** What a send asks for beyond the draft: a release time, for a scheduled send. */
export interface ComposeSendInput {
  sendAt?: string | null;
}
